import React, { useCallback, useMemo } from "react";

export type DietaryFilter = "veg" | "vegan" | "combo";

interface DietaryFiltersProps {
  activeDietaryFilters: Set<DietaryFilter>;
  onDietaryFilterToggle: (filter: DietaryFilter) => void;
  hasEnabledCombos?: boolean;
}

const FILTER_LABELS: Record<DietaryFilter, string> = {
  veg: "🥬 Veg",
  vegan: "🌱 Vegan",
  combo: "🍽️ Combo",
};

// ----------------- Logic Hook -----------------
export const useDietaryFilterLogic = () => {
  const handleDietaryFilterToggle = useCallback((
    filter: DietaryFilter,
    currentFilters: Set<DietaryFilter>,
    setFilters: (filters: Set<DietaryFilter>) => void
  ) => {
    const newFilters = new Set(currentFilters);

    switch (filter) {
      case "veg":
        if (newFilters.has("veg")) {
          newFilters.delete("veg");
          newFilters.delete("vegan");
        } else {
          newFilters.add("veg");
          newFilters.add("vegan");
        }
        break;

      case "vegan":
        if (newFilters.has("vegan")) {
          newFilters.delete("vegan");
          // Don't delete "veg" - keep it active to show only veg items
        } else {
          newFilters.add("vegan");
        }
        break;

      default:
        newFilters.has(filter)
          ? newFilters.delete(filter)
          : newFilters.add(filter);
    }

    setFilters(newFilters);
  }, []);

  return { handleDietaryFilterToggle };
};

// ----------------- Data Filtering -----------------
export const applyDietaryFilters = (
  menuData: any[],
  comboData: any[],
  activeDietaryFilters: Set<DietaryFilter>
): any[] => {
  if (!activeDietaryFilters.size) return menuData;

  const filteredItems: any[] = [];

  // Helper function to normalize values to string
  const normalizeValue = (value: any): string => {
    if (value == null) return '';
    return value.toString().toLowerCase().trim();
  };

  // Combo filter
  if (activeDietaryFilters.has("combo")) {
    const enabledCombos = (comboData ?? []).filter((c: any) => c.is_enabled);
    const comboItems = enabledCombos.map((combo: any) => ({
      pk_id: combo.combo_id,
      name: combo.title,
      description: combo.description,
      price: combo.combo_price?.replace("$", ""),
      image: combo.combo_image,
      category: "Combo",
      food_type: "combo",
      is_combo: true,
      combo_data: combo,
      level1_category: "Combo",
      level2_category: "Combo",
      is_vegan: "no",
      modifiers_list: [],
    }));
    filteredItems.push(...comboItems);
  }

  const hasVeg = activeDietaryFilters.has("veg");
  const hasVegan = activeDietaryFilters.has("vegan");

  if (hasVegan && hasVeg) {
    // Show both vegan and vegetarian items
    filteredItems.push(
      ...menuData.filter((item) => {
        const isVeganValue = normalizeValue(item.is_vegan);
        const foodTypeValue = normalizeValue(item.food_type);
        const dietaryIsVegan = item.dietary?.isVegan;
        
        // Check if item is vegan
        const isVegan = isVeganValue === "yes" || 
                       isVeganValue === "true" || 
                       isVeganValue === "1" ||
                       dietaryIsVegan === true;
        
        // Check if item is vegetarian
        const isVegetarian = (isVeganValue === "no" || 
                             isVeganValue === "false" || 
                             isVeganValue === "0" ||
                             dietaryIsVegan === false) &&
                            (foodTypeValue === "veg" || 
                             foodTypeValue === "vegetarian");
        
        return isVegan || isVegetarian;
      })
    );
  } else if (hasVeg && !hasVegan) {
    // Show only vegetarian items (exclude vegan)
    filteredItems.push(
      ...menuData.filter((item) => {
        const isVeganValue = normalizeValue(item.is_vegan);
        const foodTypeValue = normalizeValue(item.food_type);
        const dietaryIsVegan = item.dietary?.isVegan;
        
        // Must NOT be vegan
        const isNotVegan = isVeganValue === "no" || 
                          isVeganValue === "false" || 
                          isVeganValue === "0" ||
                          dietaryIsVegan === false;
        
        // Must be vegetarian
        const isVegetarian = foodTypeValue === "veg" || 
                            foodTypeValue === "vegetarian";
        
        return isNotVegan && isVegetarian;
      })
    );
  } else if (hasVegan && !hasVeg) {
    // Show only vegan items
    filteredItems.push(
      ...menuData.filter((item) => {
        const isVeganValue = normalizeValue(item.is_vegan);
        const dietaryIsVegan = item.dietary?.isVegan;
        
        return isVeganValue === "yes" || 
               isVeganValue === "true" || 
               isVeganValue === "1" ||
               dietaryIsVegan === true;
      })
    );
  }

  return filteredItems;
};

// ----------------- Component -----------------
const DietaryFilters: React.FC<DietaryFiltersProps> = React.memo(
  ({ activeDietaryFilters, onDietaryFilterToggle, hasEnabledCombos = false }) => {
    const availableFilters = useMemo(
      () =>
        (Object.keys(FILTER_LABELS) as DietaryFilter[]).filter(
          (f) => f !== "combo" || hasEnabledCombos
        ),
      [hasEnabledCombos]
    );

    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-6 sm:gap-4 md:gap-6">
          {availableFilters.map((filter) => {
            const active = activeDietaryFilters.has(filter);

            return (
              <label
                key={filter}
                className="flex items-center gap-3 cursor-pointer select-none"
              >
                {/* Hidden checkbox for accessibility */}
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => onDietaryFilterToggle(filter)}
                  className="sr-only"
                  aria-label={`Toggle ${FILTER_LABELS[filter]} filter`}
                />

                {/* Toggle switch */}
                <div
                  className={`w-11 h-6 rounded-full flex items-center transition-colors duration-200 ease-in-out ${
                    active ? "bg-red-500" : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ease-in-out ${
                      active ? "translate-x-5" : "translate-x-0.5"
                    }`}
                  />
                </div>

                {/* Label */}
                <span
                  className={`text-sm font-medium ${
                    active ? "text-gray-900" : "text-gray-700"
                  }`}
                >
                  {FILTER_LABELS[filter]}
                </span>
              </label>
            );
          })}
        </div>
      </div>
    );
  }
);

DietaryFilters.displayName = 'DietaryFilters';

export default DietaryFilters;
